import type {AgentSkill} from "@/types/skills";

export const isReadOnlySkill = (skill: AgentSkill): boolean => skill.metadata?.readOnly === true;

export {humanizeName} from "@repo/ui/gailgpt/skills";

export const isBinaryFile = (name: string) =>
   /\.(pdf|png|jpg|jpeg|gif|webp|docx|xls|xlsx|zip|tar|gz)$/i.test(name);

/**
 * Files we can safely read with `file.text()` and pass to the LLM as inline
 * text. Anything else (PDFs, images, office docs) needs to be sent as a
 * file content part. Centralized so all skill upload/generate flows agree.
 */
export const isTextLikeFilename = (name: string) =>
   /\.(md|markdown|py|sh|bash|txt|json|yaml|yml|toml|js|jsx|ts|tsx|csv|html|css|xml)$/i.test(name);

/** Markdown files (at any casing) that can serve as a skill definition. */
export const isMarkdownFilename = (name: string) => /\.(md|markdown)$/i.test(name);

/**
 * Quote YAML scalar values in SKILL.md frontmatter that contain colons
 * (or other special characters) which would otherwise cause a
 * "plain scalar value, found invalid mapping" parse error on the backend.
 *
 * Works line-by-line so it doesn't need a full YAML parser — the parser
 * itself would choke on the same input.
 */
export function sanitizeSkillMdFrontmatter(content: string): string {
   const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---/.exec(content);
   if (!fmMatch) return content;

   const sanitized = fmMatch[1].replaceAll(
      /^([ \t]*\w[\w-]*:[ \t]+)(.+)$/gm,
      (_line, prefix: string, value: string) => {
         if (/^["']/.test(value)) return _line; // already quoted
         if (!/:\s/.test(value)) return _line;   // no problematic colon
         return `${prefix}"${value.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"`;
      }
   );

   return content.replace(fmMatch[0], `---\n${sanitized}\n---`);
}

// .skill / .zip bundles are zip archives that must be extracted before upload.
export const isSkillBundle = (file: File) => /\.(skill|zip)$/i.test(file.name);

export const isCaseInsensitiveSkillMd = (name: string) =>
   name.toLowerCase() === "skill.md";

/**
 * Error thrown for client-side skill upload failures that have a dedicated
 * i18n key. The UI translates `i18nKey`; the superclass `message` is an
 * English fallback used in logs and tests.
 */
export class LocalizedSkillError extends Error {
   constructor(public readonly i18nKey: string, fallback: string) {
      super(fallback);
      this.name = "LocalizedSkillError";
   }
}

/**
 * Result of identifying which file in an upload batch is the skill definition.
 *  - `found`: exactly one candidate (`file` may still need renaming to SKILL.md)
 *  - `ambiguous`: several root-level Markdown files and no clear winner
 *  - `missing`: no root-level Markdown file at all
 */
export type SkillDefinitionResolution =
   | {kind: "found"; file: File}
   | {kind: "ambiguous"; candidates: File[]}
   | {kind: "missing"};

/**
 * Decide which uploaded file is the skill definition. Only root-level
 * Markdown files count — Markdown under a subdirectory (from an extracted
 * bundle) is a supporting reference, never the definition. A case-insensitive
 * `skill.md` always wins; otherwise a lone root Markdown file is the
 * definition; anything else is ambiguous.
 */
export function resolveSkillDefinition(files: File[]): SkillDefinitionResolution {
   const rootMarkdown = files.filter(
      (f) => !f.name.includes("/") && isMarkdownFilename(f.name)
   );
   const canonical = rootMarkdown.filter((f) => isCaseInsensitiveSkillMd(f.name));
   if (canonical.length > 1) return {kind: "ambiguous", candidates: canonical};
   if (canonical.length === 1) return {kind: "found", file: canonical[0]};
   if (rootMarkdown.length === 1) return {kind: "found", file: rootMarkdown[0]};
   if (rootMarkdown.length > 1) return {kind: "ambiguous", candidates: rootMarkdown};
   return {kind: "missing"};
}

/**
 * Rename the resolved skill definition file to the canonical SKILL.md so the
 * Galactus backend contract (and every downstream consumer that fetches the
 * SKILL.md path) stays unchanged regardless of the original filename.
 * Throws a LocalizedSkillError when the definition is missing or ambiguous.
 */
export function normalizeSkillDefinitionFiles(
   files: File[],
   errorKeys: {missingKey: string; ambiguousKey: string} = {
      missingKey: "upload.skillMdRequired",
      ambiguousKey: "upload.definitionAmbiguous",
   }
): File[] {
   const resolution = resolveSkillDefinition(files);
   if (resolution.kind === "ambiguous") {
      throw new LocalizedSkillError(
         errorKeys.ambiguousKey,
         "Multiple Markdown files could be the skill definition; rename one to SKILL.md or remove the extras"
      );
   }
   if (resolution.kind === "missing") {
      throw new LocalizedSkillError(
         errorKeys.missingKey,
         "A Markdown skill definition file (usually named SKILL.md) is required"
      );
   }
   if (resolution.file.name === "SKILL.md") return files;
   return files.map((f) =>
      f === resolution.file
         ? new File([f], "SKILL.md", {type: f.type, lastModified: f.lastModified})
         : f
   );
}

/**
 * Translate a thrown error for user display. Shared across all skill upload
 * flows so alerts stay localized consistently:
 *  - `LocalizedSkillError` → translate its i18n key (client-side failures)
 *  - other `Error` with a message → pass through (server-provided detail,
 *    which the API already renders in the server's chosen wording)
 *  - anything else → generic translated fallback
 */
export function getSkillErrorMessage(
   e: unknown,
   t: (key: string) => string,
   fallbackKey: string
): string {
   if (e instanceof LocalizedSkillError) return t(e.i18nKey);
   if (e instanceof Error && e.message) return e.message;
   return t(fallbackKey);
}

/**
 * Strip a single common leading directory from zip entries
 * (e.g. "gail-ai/SKILL.md" → "SKILL.md"). Anthropic-style .skill
 * bundles wrap everything in a single root folder.
 */
export function stripCommonPrefix(paths: string[]): (path: string) => string {
   if (paths.length === 0) return (p) => p;
   const firstSegment = paths[0].split("/")[0];
   const allShareRoot = paths.every((p) => p.startsWith(`${firstSegment}/`));
   if (!allShareRoot) return (p) => p;
   return (p) => p.slice(firstSegment.length + 1);
}

// Zip-bomb guards. Skills are primarily markdown plus a handful of small
// reference files — these caps are generous for real skills but keep a
// malicious archive from hanging the browser tab.
const MAX_BUNDLE_ENTRIES = 500;
const MAX_BUNDLE_BYTES = 50 * 1024 * 1024; // 50 MB decompressed

/**
 * Extract a .skill / .zip bundle into individual File objects.
 * Renames the skill definition file to SKILL.md so the backend stays happy.
 * Throws if the bundle is empty, has no root-level Markdown definition file,
 * or has several root-level Markdown files with no clear definition.
 */
export async function extractSkillBundle(bundle: File): Promise<File[]> {
   // Lazy-load jszip — it's only needed when uploading a bundle.
   const {default: JSZip} = await import("jszip");

   // JSZip throws technical, English-only errors for corrupt archives
   // (e.g. "End of data reached"). Wrap as a localized error so the UI
   // shows a user-friendly, translated message instead.
   let zip: Awaited<ReturnType<typeof JSZip.loadAsync>>;
   try {
      zip = await JSZip.loadAsync(bundle);
   } catch (e) {
      throw new LocalizedSkillError(
         "upload.bundleInvalid",
         e instanceof Error ? `Invalid bundle: ${e.message}` : "Invalid bundle"
      );
   }

   const entries = Object.values(zip.files).filter((entry) => !entry.dir);
   if (entries.length === 0) {
      throw new LocalizedSkillError("upload.bundleEmpty", "Bundle is empty");
   }
   if (entries.length > MAX_BUNDLE_ENTRIES) {
      throw new LocalizedSkillError(
         "upload.bundleTooManyFiles",
         `Bundle has too many files (max ${MAX_BUNDLE_ENTRIES})`
      );
   }

   const stripPrefix = stripCommonPrefix(entries.map((e) => e.name));

   // Track cumulative decompressed size so a zip bomb bails early instead of
   // exhausting memory. Using a shared counter across parallel decompressions
   // is fine — worst case we over-decompress by one entry before throwing.
   let totalBytes = 0;
   const extracted = await Promise.all(
      entries.map(async (entry) => {
         const blob = await entry.async("blob");
         totalBytes += blob.size;
         if (totalBytes > MAX_BUNDLE_BYTES) {
            throw new LocalizedSkillError(
               "upload.bundleTooLarge",
               `Bundle is too large (max ${MAX_BUNDLE_BYTES} bytes decompressed)`
            );
         }
         const path = stripPrefix(entry.name);
         const finalName = isCaseInsensitiveSkillMd(path) ? "SKILL.md" : path;
         return new File([blob], finalName);
      })
   );

   return normalizeSkillDefinitionFiles(extracted, {
      missingKey: "upload.bundleMissingSkillMd",
      ambiguousKey: "upload.definitionAmbiguous",
   });
}

/**
 * Read a file as a UTF-8 string. Uses FileReader rather than `file.text()`
 * because jsdom (the test environment) does not implement Blob.text().
 */
export function fileToText(file: File): Promise<string> {
   return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
         const result = reader.result;
         if (typeof result !== "string") {
            reject(new Error(`FileReader returned non-string for ${file.name}`));
            return;
         }
         resolve(result);
      };
      reader.onerror = () => reject(reader.error ?? new Error(`Failed to read ${file.name}`));
      reader.readAsText(file);
   });
}

/**
 * Split user-provided reference files into:
 *  - `context`: concatenated text of files we can read directly (md/json/etc.)
 *  - `attachmentFiles`: raw File objects for the LLM file API (sent as-is in
 *    a multipart upload — no base64 round-trip).
 *
 * Text reads run in parallel; FileReader operations don't compete for CPU on
 * the main thread.
 */
export async function partitionFilesForGeneration(
   files: File[]
): Promise<{context: string; attachmentFiles: File[]}> {
   const entries = await Promise.all(
      files.map(async (file) =>
         isTextLikeFilename(file.name)
            ? {kind: "text" as const, name: file.name, text: await fileToText(file)}
            : {kind: "binary" as const, file}
      )
   );

   let context = "";
   const attachmentFiles: File[] = [];
   for (const entry of entries) {
      if (entry.kind === "text") {
         context += `\n--- ${entry.name} ---\n${entry.text}\n`;
      } else {
         attachmentFiles.push(entry.file);
      }
   }

   return {context, attachmentFiles};
}
